/** R4 (spec §6.1): the grid's proposed-value display mode + the pure value resolver.
 *
 * Kept in its own leaf module so CrmListGrid stays a component-only export surface (react-refresh)
 * and the resolver is unit-testable without importing AG-Grid. 'current' shows committed values
 * (default); 'ai' shows the PROPOSED value in cells that carry a field proposal. It is persisted
 * with the saved view because it also controls proposed-row visibility.
 */

export type ValueMode = 'current' | 'ai';

// The proposal riding on a cell, if any (M3.4 field proposals; `_proposals` is keyed by field_key).
export function cellProposal(
  row: Record<string, unknown> | null | undefined, fieldKey: string | undefined,
): { value: unknown } | undefined {
  if (!row || !fieldKey) return undefined;
  return (row._proposals as Record<string, { value: unknown }> | undefined)?.[fieldKey];
}

// The ONE place proposed-vs-current is resolved for DISPLAY. NEVER wire this into valueGetter —
// sort/filter/quick-filter/edit must keep operating on committed values; this is display-only,
// threaded through the default valueFormatter AND every custom cell renderer so a green cell can't
// print the current value while claiming to show the AI one.
export function displayValueForMode(
  row: Record<string, unknown> | null | undefined, fieldKey: string | undefined,
  rawValue: unknown, mode: ValueMode | undefined,
): unknown {
  if (mode !== 'ai') return rawValue;
  const prop = cellProposal(row, fieldKey);
  return prop ? prop.value : rawValue;
}

/** How one value of a multi-valued cell relates to the proposal (spec §5.9 / P12). */
export type MultiValueState = 'added' | 'removed' | 'unchanged';

/** A multi-valued cell's values in render order, each tagged with what the proposal does to it. */
export interface MultiValueDiffEntry {
  value: string;
  state: MultiValueState;
}

/**
 * The set-diff of a multi-valued proposal (spec §5.9).
 *
 * A `multi_enum` proposal is stored as the WHOLE new list, so rendering it naively paints every
 * proposed value as new and strikes every current one — including the values that did not change. The
 * owner's example: the AI added *Environmental Services* to `themes` and the display struck
 * *Waste-to-Value* and *Sustainable Agriculture*, which it kept. The semantics are ADD/REMOVE, so the
 * render has to be too.
 *
 * ⚠ **Set equality, not list equality.** A reorder with the same membership must come back entirely
 * `unchanged` — an index-by-index diff would paint the whole cell and reproduce the original bug in a
 * new place. Comparison is on the trimmed, case-folded string so `"Waste-to-Value"` and
 * `"waste-to-value "` are one value; the ORIGINAL spelling is what renders.
 *
 * Order is deliberate and stable: kept values first, in the proposal's order, then the removed ones
 * after. A struck value that jumps to the top of the cell is harder to read than one appended, and the
 * proposal's order is the order the operator is being asked to approve.
 *
 * Lives beside `displayValueForMode` because the diff belongs to the value PAIR, not to any surface —
 * the grid cell, the review bar and the record panel must not each derive it and disagree about what
 * changed (the catch-all-flip lesson).
 */
export function multiValueDiff(current: unknown, proposed: unknown): MultiValueDiffEntry[] {
  const toList = (v: unknown): string[] =>
    (Array.isArray(v) ? v : v == null || v === '' ? [] : [v])
      .map((x) => (x == null ? '' : String(x)))
      .filter((s) => s.trim() !== '');
  const key = (s: string) => s.trim().toLowerCase();

  const cur = toList(current);
  const next = toList(proposed);
  const curKeys = new Set(cur.map(key));
  const nextKeys = new Set(next.map(key));

  const out: MultiValueDiffEntry[] = next.map((value) => ({
    value,
    state: curKeys.has(key(value)) ? ('unchanged' as const) : ('added' as const),
  }));
  for (const value of cur) {
    if (!nextKeys.has(key(value))) out.push({ value, state: 'removed' });
  }
  return out;
}

// true when a cell should render its proposed value (AI mode + a live proposal) — drives the
// bold-green text class + the read-only-in-AI-mode edit guard.
export function showsProposedValue(
  params: { context?: { valueMode?: ValueMode }; data?: Record<string, unknown>; colDef?: { field?: string } },
): boolean {
  return params.context?.valueMode === 'ai' && !!cellProposal(params.data, params.colDef?.field);
}
